import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { useSafeTimeout } from "@uplift-ltd/use-safe-timeout";
import React, { Fragment, useState } from "react";
import { useUserContext } from "hooks/useUserContext";
import Loading from "../Loading";
import styles from "./Modal.module.css";

export interface ModalProps {
  children: React.ReactNode;
  open: boolean;
  onRequestClose: () => void;
  title: string;
  subtitle?: string;
  requiresAuth?: boolean;
}

// Usage:
// const { open, setOpen, onRequestClose } = useModal({
//   onClose: () => console.log("Bye Felicia"),
// });
// return (
//   <Modal open={open} onRequestClose={onRequestClose}>
//     <p>Hello world</p>
//   </Modal>
// );
export default function Modal({
  children,
  open,
  onRequestClose,
  title,
  subtitle,
  requiresAuth = false,
}: ModalProps) {
  // This is a hacky solution until we can mark the user context as
  // stale after login so that the query will refetch
  const user = useUserContext();

  const isLoadingUser = requiresAuth && user.loading;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className={styles.dialog}
        open={open}
        onClose={() => onRequestClose()}
      >
        <div className={styles.container}>
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className={styles.overlay} />
          </Transition.Child>

          <div className={styles.centerTrick} />

          <div className={styles.modal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-500"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className={styles.modalContainer}>
                <div className={styles.modalHeader}>
                  <div className={styles.titleContainer}>
                    <Dialog.Title className={styles.modalTitle}>{title}</Dialog.Title>
                    {subtitle && (
                      <Dialog.Title className={styles.modalSubtitle}>{subtitle}</Dialog.Title>
                    )}
                  </div>
                  <div className={styles.modalClose}>
                    <button
                      className={styles.modalCloseButton}
                      type="button"
                      onClick={() => onRequestClose()}
                    >
                      <span className="sr-only">Close panel</span>
                      <XIcon className={styles.modalCloseIcon} aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <div className={styles.modalBody}>{isLoadingUser ? <Loading /> : children}</div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export interface UseModalOptions {
  initialOpen?: boolean;
  onClose?: () => void;
}

export function useModal({ initialOpen, onClose }: UseModalOptions) {
  const [open, setOpen] = useState(!!initialOpen);
  const setSafeTimeout = useSafeTimeout();

  const onRequestClose = (onCloseBefore?: () => void) => {
    setOpen(false);
    setSafeTimeout(() => {
      onCloseBefore?.();
      onClose?.();
    }, 500);
  };

  return { open, setOpen, onRequestClose };
}
